import React, { useEffect, useState } from 'react';
import {
	Box,
	Card,
	Typography,
	Button,
	TextField,
	Grid,
	Chip,
	Alert,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Checkbox,
	Radio,
	useMediaQuery,
	useTheme,
	Slider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { getCustomerById } from '../services/customers';
import { useLocation, useNavigate } from 'react-router-dom';
import { createCustomerSessionDetails, getCustomerSessionDetails, getCustomerSessionDetailsByCustomerId } from '../services/customerSessionDetails';
import { getAssessmentCountsByCustomer, getLatestHADSScoresByCustomer, getPostSessionScores } from '../services/customerAssessment';
import { getEffectiveAccountId } from '../utils/accountId';

const FormContainer = styled(Card)(({ theme }) => ({
	background: 'white',
	borderRadius: '20px',
	padding: '30px',
	boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
	marginBottom: '20px',
	[theme.breakpoints.down('sm')]: {
		borderRadius: '12px',
		padding: '16px',
		marginBottom: '16px',
	},
}));

const ArmBadge = styled(Chip)(({ arm }) => ({
	padding: '5px 15px',
	borderRadius: '20px',
	fontSize: '0.85em',
	fontWeight: 600,
	color: 'white',
	background: arm === 'test' ? '#4caf50' : arm === 'control' ? '#ff9800' : '#9e9e9e',
}));

const SubmitButton = styled(Button)(({ theme }) => ({
	padding: '12px 30px',
	background: 'linear-gradient(135deg, #667eea, #764ba2)',
	color: 'white',
	borderRadius: '10px',
	fontSize: '16px',
	fontWeight: 600,
	textTransform: 'none',
	'&:hover': {
		transform: 'translateY(-2px)',
		boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)',
	},
	[theme.breakpoints.down('sm')]: {
		padding: '14px 20px',
		fontSize: '14px',
	},
}));

const hasScoreValue = (score) => score !== null && score !== undefined && score !== "";
const formatScore = (score) => (hasScoreValue(score) ? score : "N/A");

const isHADSAssessment = (assessmentType) =>
	String(assessmentType || "").trim().toLowerCase().includes("hads");

// Same fallback as Analytics: session assessment rows first, then the HADS scores snapshotted on the session.
const findLatestHADSScores = async (sessionList) => {
	for (const session of sessionList) {
		const scores = await getPostSessionScores(session.id);
		const hads = scores.find(
			(score) =>
				isHADSAssessment(score.assessment_type) &&
				(hasScoreValue(score.anxiety_score) || hasScoreValue(score.depression_score))
		);

		if (hads) {
			return {
				anxiety: hads.anxiety_score,
				depression: hads.depression_score,
				treatmentCycle: session.treatment_cycle,
			};
		}

		const details = await getCustomerSessionDetails(session.id);
		if (hasScoreValue(details?.hads_anxiety) || hasScoreValue(details?.hads_depression)) {
			return {
				anxiety: details.hads_anxiety,
				depression: details.hads_depression,
				treatmentCycle: details.treatment_cycle,
			};
		}
	}

	return null;
};

function TreatmentSessions() {
	const navigate = useNavigate();
	const location = useLocation();
	const customer = location.state?.customer;
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [selectedPatient, setSelectedPatient] = useState(null);
	const [patient, setPatient] = useState(null);
	const [selectedCycle, setSelectedCycle] = useState('');
	const rawAccountId = localStorage.getItem("account_id");
	// Accounts 4/5 behave like accounts 2/3.
	const accountId = getEffectiveAccountId(rawAccountId);
	const [latestHADSScores, setLatestHADSScores] = useState(null);
	const [assessmentCounts, setAssessmentCounts] = useState({ hads: 0, psqi: 0, kdqol: 0 });
	const [formData, setFormData] = useState({
		treatmentCycle: "",
		bloodPressure: "",
		systolic: "",
		diastolic: "",
		heartRate: "",
		painScale: "",
		moodRating: "",
		sleepQuality: "",
		assessment_type: "",
	});

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	useEffect(() => {
		const fetchCustomer = async () => {
			if (customer?.id) {
				try {
					const data = await getCustomerById(customer?.id, rawAccountId);

					setPatient(data);

					console.log("Fetched patient:", data);
				} catch (error) {
					console.error(error);
				}
			}
		};

		fetchCustomer();
	}, [customer?.id]);

	useEffect(() => {
		const fetchLatestAssessmentInfo = async () => {
			if (!customer?.id) return;

			try {
				const latestScores = await getLatestHADSScoresByCustomer(customer.id);
				if (latestScores && (hasScoreValue(latestScores.anxiety_score) || hasScoreValue(latestScores.depression_score))) {
					setLatestHADSScores({
						anxiety: latestScores.anxiety_score,
						depression: latestScores.depression_score,
						treatmentCycle: latestScores.treatment_cycle,
					});
				} else {
					const sessions = await getCustomerSessionDetailsByCustomerId(customer.id);
					setLatestHADSScores(await findLatestHADSScores(sessions || []));
				}
			} catch (error) {
				console.error("Error fetching latest HADS scores:", error);
				setLatestHADSScores(null);
			}

			try {
				const counts = await getAssessmentCountsByCustomer(customer.id);
				setAssessmentCounts(counts || { hads: 0, psqi: 0, kdqol: 0 });
			} catch (error) {
				console.error("Error fetching assessment counts:", error);
				setAssessmentCounts({ hads: 0, psqi: 0, kdqol: 0 });
			}
		};

		fetchLatestAssessmentInfo();
	}, [customer?.id]);

	const allFieldsFilled =
		// patient?.treatment_arm === 'test'
		// 	?
		formData.bloodPressure &&
		formData.heartRate &&
		formData.painScale &&
		formData.systolic &&
		formData.diastolic &&
		// formData.moodRating &&
		formData.nausea &&
		formData.fatigue &&
		formData.anxiety &&
		formData.depression &&
		formData.appetiteloss &&
		formData.sleepQuality &&
		formData.assessment_type
	// : formData.treatmentCycle;

	const requiredFields = [
		{ key: "bloodPressure", label: "Blood Pressure" },
		{ key: "heartRate", label: "Heart Rate" },
		{ key: "painScale", label: "Pain Scale" },
		{ key: "nausea", label: "Nausea" },
		{ key: "fatigue", label: "Fatigue" },
		{ key: "anxiety", label: "Anxiety" },
		{ key: "depression", label: "Depression" },
		{ key: "appetiteloss", label: "Appetite Loss" },
		{ key: "sleepQuality", label: "Sleep Quality" },
		// { key: "assessment_type", label: "Assessment Type" },
	];

	const validateFieldsInSequence = () => {
		for (const field of requiredFields) {
			const value = formData[field.key];

			// Special case: assessment_type is array
			// if (field.key === "assessment_type" && (!value || value.length === 0)) {
			// 	alert(`Please fill: ${field.label}`);
			// 	return false;
			// }

			if (!value) {
				alert(`Please fill: ${field.label}`);
				return false;
			}
		}
         		const sys = Number(formData.systolic);
		const dia = Number(formData.diastolic);
		const hr = Number(formData.heartRate);

		if (sys < 40 || sys > 230) {
			alert("Systolic BP must be between 40 and 230");
			return false;
		}
		if (dia < 30 || dia > 180) {
			alert("Diastolic BP must be between 30 and 180");
			return false;
		}
		if (hr < 40 || hr > 150) {
			alert("Heart rate must be between 40 and 150");
			return false;
		}
		return true;
	};

	const handleSubmit = async () => {

		const isValid = validateFieldsInSequence();
		if (!isValid) return;

		try {
			const payload = {
				account_id: Number(rawAccountId),
				treatment_arm: patient?.treatment_arm,
				customer_id: patient?.id,
				hads_anxiety: patient?.anxiety_score,
				hads_depression: patient?.depression_score,
				treatment_cycle: patient?.treatment_cycle + 1,
				// blood_pressure: Number(formData.systolic),
				blood_pressure: formData.systolic && formData.diastolic
					? `${formData.systolic}/${formData.diastolic}`
					: "",
				heart_rate: Number(formData.heartRate),
				pain_scale: Number(formData.painScale),
				mood_rating: formData.moodRating || "none",
				nausea: formData.nausea,
				fatigue: formData.fatigue,
				anxiety: formData.anxiety,
				depression: formData.depression,
				appetite_loss: formData.appetiteloss,
				sleep_quality: formData.sleepQuality,
				preferred_language: patient.preferred_language,
				//assessment_type: formData.assessment_type.map((key) => ({
					assessment_type: formData.assessment_type?.length > 0 ?
					formData.assessment_type.map((key) => ({
						title: key,
						status: "pending",
					})) : [],

			};

			console.log("📤 Payload:", payload);
			const response = await createCustomerSessionDetails(payload);
			console.log("✅ API Response:", response);

			alert("Session created successfully!");

			navigate("/view-pin", {
				state: {
					sessionId: response.id,
					name: `${response.first_name} ${response.last_name}`, // combined full name
					// preferredLanguage: response.preferred_language,
					preferredLanguage: patient.preferred_language,   // send preferred language
					treatmentArm: response.treatment_arm,
					pin: response.pin            // send treatment type
				},
			});

		} catch (error) {
			console.error("❌ Error creating session:", error);
			alert("Failed to create session. Please try again.");
		}
	};

	const assessments =
	accountId === "1"
		? [
			{
				title: "HADS Assessment",
				subtitle: "Hospital Anxiety and Depression Scale",
				key: "hads",
			},
			{
				title: "FACT-G7 Assessment",
				subtitle: "Functional Assessment of Cancer Therapy",
				key: "factg7",
			},
			{
				title: "EORTC QLQ-C30",
				subtitle: "European Quality of Life Questionnaire",
				key: "eortc",
			},
		]
		: accountId === "3"
		? [
			{
				title: "HADS Assessment",
				subtitle: "Hospital Anxiety and Depression Scale",
				key: "hads",
			},
			{
				title: "PSQI Assessment",
				subtitle: "Sleep Quality measurement",
				key: "psqi",
			},
			rawAccountId === "4"
				? {
					title: "EORTC QLQ-C30",
					subtitle: "European Quality of Life Questionnaire",
					key: "eortc",
				}
				: {
					title: "KDQOL-36 Assessment",
					subtitle: "Kidney Disease and Quality of Life",
					key: "kdqol36",
				},
		]
		: [
			{
				title: "HADS Assessment",
				subtitle: "Hospital Anxiety and Depression Scale",
				key: "hads",
			},
			{
				title: "PSQI Assessment",
				subtitle: "Sleep Quality measurement",
				key: "psqi",
			},
			{
				title: "EORTC QLQ-C30",
				subtitle: "European Quality of Life Questionnaire",
				key: "eortc",
			},
		];

	return (
		<FormContainer>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					mb: { xs: 1.5, sm: 2 },
					flexWrap: { xs: 'wrap', sm: 'nowrap' },
					gap: { xs: 1, sm: 0 }
				}}
			>
				<Typography
					variant="h6"
					fontWeight={400}
					sx={{
						color: "#1f2937",
						fontSize: { xs: '1.1rem', sm: '1.25rem' }
					}}
				>
					Treatment Session
				</Typography>

				<Button
					variant="contained"
					onClick={() => navigate("/patients")}
					sx={{
						bgcolor: "#4A90E2",
						"&:hover": { bgcolor: "#357ABD" },
						borderRadius: 2,
						px: { xs: 2, sm: 2.5 },
						py: { xs: 0.8, sm: 1 },
						textTransform: "none",
						fontWeight: 600,
						fontSize: { xs: "0.85rem", sm: "0.875rem" },
						boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
						whiteSpace: "nowrap",
						minWidth: "auto",
						height: { xs: 36, sm: "auto" },
					}}
				>
					Back
				</Button>
			</Box>

			<Box sx={{
				background: '#f8f9ff',
				p: { xs: 2, sm: 3 },
				borderRadius: { xs: '8px', sm: '10px' },
			}}>
				<Box display="flex" justifyContent="space-between" flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 0 }}>
					<Box display="flex" flexDirection="column" gap={2}>
						<Typography
							variant="h6"
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1,
								color: "#1f2937",
								fontSize: { xs: '1.05rem', sm: '1.2rem' }
							}}
						>
							<img
								src={
									patient?.gender?.toLowerCase() === 'male'
										? '/man.png'
										: patient?.gender?.toLowerCase() === 'female'
											? '/woman.png'
											: '/user.png'
								}
								alt="gender-icon"
								style={{ width: 24, height: 24 }}
							/>

							{patient?.firstname} {patient?.lastname} | {patient?.age || 'N/A'} years
						</Typography>
					<Typography
    sx={{
        color: '#444',
        fontSize: { xs: '0.85rem', sm: '1rem' },
        fontWeight: 600,
        mb: 0.5,
    }}
>
  
</Typography>

						<Typography
							sx={{
								color: '#444',
								fontSize: { xs: '0.85rem', sm: '1rem' },
								fontWeight: 600,
								mb: 0.5,
							}}
						>
							Latest HADS Score — Anxiety: {formatScore(latestHADSScores?.anxiety)} | Depression: {formatScore(latestHADSScores?.depression)}
						</Typography>

						{accountId === "3" && (
							<Typography
		sx={{
			color: '#444',
			fontSize: { xs: '0.85rem', sm: '1rem' },
			fontWeight: 600,
			mb: 0.5,
		}}
	>
		Number of Assessments Administered — HADS: {String(assessmentCounts.hads).padStart(2, "0")} | PSQI: {String(assessmentCounts.psqi).padStart(2, "0")} | {rawAccountId === "4"
			? `EORTC: ${String(assessmentCounts.eortc).padStart(2, "0")}`
			: `KDQOL: ${String(assessmentCounts.kdqol).padStart(2, "0")}`} | Treatment Cycle: {formatScore(latestHADSScores?.treatmentCycle ?? patient?.treatment_cycle)}
	</Typography>
						)}
						{/* <Typography sx={{
							color: '#666',
							fontSize: { xs: '0.85rem', sm: '1rem' },
							lineHeight: { xs: 1.6, sm: 1.5 }
						}}>
							Gender: {patient?.gender || 'N/A'} |
							Language: {patient?.preferred_language?.toUpperCase() || 'N/A'} |
							Treatment Cycle: {patient?.treatment_cycle + 1}
						</Typography> */}
					</Box>

					{/* <Box
						sx={{
							p: 2,
							border: '1px solid #e5e7eb',
							borderRadius: '10px',
							backgroundColor: '#f9fafb',
						}}
					>
						<Typography
							sx={{
								color: '#1a1a1a',
								fontSize: { xs: '0.95rem', sm: '1.1rem' },
								mb: 1,
							}}
						>
							Pre-HADS Scores
						</Typography>

						<Typography
							sx={{
								color: '#444',
								fontSize: { xs: '0.85rem', sm: '1rem' },
								mb: 0.5,
							}}
						>
							Anxiety: {patient?.anxiety_score ?? 'N/A'}/21 | Depression: {patient?.depression_score ?? 'N/A'}/21
						</Typography>
					</Box> */}

					<Typography sx={{
						color: '#666',
						fontSize: { xs: '0.85rem', sm: '1rem' },
						lineHeight: { xs: 1.6, sm: 1.5 }
					}}>
						Treatment Cycle: {patient?.treatment_cycle + 1}
					</Typography>

					<Box display="flex" gap={1} alignItems="center" flexDirection="row" sx={{ width: { xs: '100%', sm: 'auto' } }}>
    <Box display="flex" gap={2} alignItems="center" flexDirection="row" sx={{ width: { xs: '100%', sm: 'auto' } }}>
				<Typography sx={{
								color: '#666',
								fontSize: { xs: '0.85rem', sm: '1rem' },
								lineHeight: { xs: 1.6, sm: 1.5 },
								textAlign: { xs: 'left', sm: 'left' }
							}}>
								Treatment Arm - </Typography>
							<ArmBadge
								arm={patient?.treatment_arm}
								label={
									patient?.treatment_arm === "test"
										? "Test Arm"
										: patient?.treatment_arm === "control"
											? "Control Arm"
											: "N/A"
								}
								sx={{
									backgroundColor:
										patient?.treatment_arm === "test"
											? "#4caf50"
											: patient?.treatment_arm === "control"
												? "#fbc02d"
												: "#9e9e9e",
									color: "white",
									fontWeight: 600,
									px: 0,
									py: 0.5,
									borderRadius: "8px",
									display: "inline-block",
								}}
							/>
						</Box>
					</Box>
				</Box>


			</Box>

			<Box sx={{
				border: '2px solid #2196f3',
				borderRadius: { xs: '8px', sm: '10px' },
				p: { xs: 2, sm: 3 },
				mt: { xs: 2, sm: 3 },
				mb: { xs: 2, sm: 3 }
			}}>
				<Typography
					variant="h6"
					fontWeight={400}
					sx={{
						color: "#1f2937",
						mb: 2,
						fontSize: { xs: '1.05rem', sm: '1.2rem' }
					}}
				>
					Session Details
				</Typography>

				<Box>
					<Box sx={{ mb: 2 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
							<Typography
								variant="subtitle1"
								sx={{
									color: '#374151',
									fontWeight: 500,
									fontSize: { xs: '0.95rem', sm: '1rem' }
								}}
							>
								Blood Pressure *
							</Typography>
							{/* Visual Indicator */}
							{formData.systolic && formData.diastolic && (
								<Box
									sx={{
										mt: 1,
										p: { xs: 1, sm: 1.5 },
										borderRadius: '8px',
										backgroundColor: (() => {
											const sys = Number(formData.systolic);
											const dia = Number(formData.diastolic);
											if (sys < 90 || dia < 60) return '#dbeafe'; // Low - blue
											if (sys < 120 && dia < 80) return '#dcfce7'; // Normal - green
											if (sys < 130 && dia < 80) return '#fef3c7'; // Elevated - yellow
											if (sys < 140 || dia < 90) return '#fed7aa'; // Stage 1 - orange
											return '#fecaca'; // Stage 2 - red
										})(),
										display: 'flex',
										alignItems: 'center',
										gap: 1,
									}}
								>
									<Box
										sx={{
											width: 8,
											height: 8,
											borderRadius: '50%',
											backgroundColor: (() => {
												const sys = Number(formData.systolic);
												const dia = Number(formData.diastolic);
												if (sys < 90 || dia < 60) return '#2563eb'; // Low - blue
												if (sys < 120 && dia < 80) return '#16a34a'; // Normal - green
												if (sys < 130 && dia < 80) return '#ca8a04'; // Elevated - yellow
												if (sys < 140 || dia < 90) return '#ea580c'; // Stage 1 - orange
												return '#dc2626'; // Stage 2 - red
											})(),
										}}
									/>
									<Typography
										variant="caption"
										sx={{
											fontWeight: 600,
											fontSize: '0.75rem',
											color: (() => {
												const sys = Number(formData.systolic);
												const dia = Number(formData.diastolic);
												if (sys < 90 || dia < 60) return '#1e40af'; // Low - dark blue
												if (sys < 120 && dia < 80) return '#166534'; // Normal - dark green
												if (sys < 130 && dia < 80) return '#854d0e'; // Elevated - dark yellow
												if (sys < 140 || dia < 90) return '#9a3412'; // Stage 1 - dark orange
												return '#991b1b'; // Stage 2 - dark red
											})(),
										}}
									>
										{(() => {
											const sys = Number(formData.systolic);
											const dia = Number(formData.diastolic);
											if (sys < 90 || dia < 60) return 'Low Blood Pressure (Hypotension)';
											if (sys < 120 && dia < 80) return 'Normal Blood Pressure';
											if (sys < 130 && dia < 80) return 'Elevated Blood Pressure';
											if (sys < 140 || dia < 90) return 'Hypertension Stage 1';
											return 'Hypertension Stage 2';
										})()}
									</Typography>
								</Box>
							)}
						</Box>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: { xs: 1, sm: 1.5 },
								p: { xs: 1.5, sm: 2 },
								border: '2px solid #e5e7eb',
								borderRadius: '12px',
								backgroundColor: '#f9fafb',
								transition: 'all 0.2s ease',
								position: 'relative',
								overflow: 'hidden',
								flexDirection: { xs: 'column', sm: 'row' },
							}}
						>
							{/* Systolic Input */}
							<Box sx={{ flex: 1, textAlign: 'center' }}>
								<Typography
									variant="caption"
									sx={{
										color: '#6b7280',
										fontWeight: 600,
										fontSize: '0.7rem',
										textTransform: 'uppercase',
										letterSpacing: '0.5px',
										display: 'block',
										mb: 0.5,
									}}
								>
									Systolic
								</Typography>
								<TextField
									name="systolic"
									type="number"
									placeholder="120"
									value={formData.systolic || ''}
									onChange={(e) => {
										const systolic = e.target.value;
										const diastolic = formData.diastolic || '';
										setFormData((prev) => ({
											...prev,
											systolic,
											bloodPressure: systolic && diastolic ? `${systolic}/${diastolic}` : '',
										}));
									}}
									variant="standard"
									inputProps={{ min: 40, max: 230, style: { textAlign: 'center' } }}
									sx={{
										'& input': {
											fontSize: { xs: '1.2rem', sm: '1.5rem' },
											fontWeight: 600,
											color: '#1f2937',
											padding: 0,
										},
										'& input::placeholder': {
											color: '#d1d5db',
											opacity: 1,
										},
									}}
								/>
							</Box>

							{/* Separator */}
							<Box
								sx={{
									fontSize: { xs: '1.5rem', sm: '2rem' },
									fontWeight: 300,
									color: '#9ca3af',
									lineHeight: 1,
									pb: 0.5,
									display: { xs: 'none', sm: 'block' },
								}}
							>
								/
							</Box>

							{/* Diastolic Input */}
							<Box sx={{ flex: 1, textAlign: 'center' }}>
								<Typography
									variant="caption"
									sx={{
										color: '#6b7280',
										fontWeight: 600,
										fontSize: '0.7rem',
										textTransform: 'uppercase',
										letterSpacing: '0.5px',
										display: 'block',
										mb: 0.5,
									}}
								>
									Diastolic
								</Typography>
								<TextField
									name="diastolic"
									type="number"
									placeholder="80"
									value={formData.diastolic || ''}
									onChange={(e) => {
										const diastolic = e.target.value;
										const systolic = formData.systolic || '';
										setFormData((prev) => ({
											...prev,
											diastolic,
											bloodPressure: systolic && diastolic ? `${systolic}/${diastolic}` : '',
										}));
									}}
									variant="standard"
									inputProps={{ min: 30, max: 180, style: { textAlign: 'center' } }}
									sx={{
										'& input': {
											fontSize: { xs: '1.2rem', sm: '1.5rem' },
											fontWeight: 600,
											color: '#1f2937',
											padding: 0,
										},
										'& input::placeholder': {
											color: '#d1d5db',
											opacity: 1,
										},
									}}
								/>
							</Box>

							{/* Unit Label */}
							<Box
								sx={{
									bgcolor: '#3b82f6',
									color: 'white',
									px: 1.5,
									py: 1,
									borderRadius: '8px',
									fontWeight: 600,
									fontSize: '0.85rem',
									minWidth: '60px',
									textAlign: 'center',
								}}
							>
								mmHg
							</Box>
						</Box>

					</Box>

					<Box sx={{ mb: 2 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
							<Typography
								variant="subtitle1"
								sx={{
									color: '#374151',
									fontWeight: 500,
									fontSize: { xs: '0.95rem', sm: '1rem' }
								}}
							>
								Heart Rate *
							</Typography>
							{/* Visual Indicator */}
							{formData.heartRate && (
								<Box
									sx={{
										mt: 1,
										p: { xs: 1, sm: 1.5 },
										borderRadius: '8px',
										backgroundColor: (() => {
											const hr = Number(formData.heartRate);
											if (hr >= 60 && hr <= 100) return '#dcfce7'; // Normal - green
											if (hr > 100 && hr <= 120) return '#fef3c7'; // Elevated - yellow
											if ((hr >= 40 && hr < 60) || (hr > 120 && hr <= 150)) return '#fed7aa'; // Borderline - orange
											return '#fecaca'; // Abnormal - red
										})(),
										display: 'flex',
										alignItems: 'center',
										gap: 1,
									}}
								>
									<Box
										sx={{
											width: 8,
											height: 8,
											borderRadius: '50%',
											backgroundColor: (() => {
												const hr = Number(formData.heartRate);
												if (hr >= 60 && hr <= 100) return '#16a34a';
												if (hr > 100 && hr <= 120) return '#ca8a04';
												if ((hr >= 40 && hr < 60) || (hr > 120 && hr <= 150)) return '#ea580c';
												return '#dc2626';
											})(),
											animation: 'blink 1.5s ease-in-out infinite',
											'@keyframes blink': {
												'0%, 100%': { opacity: 1 },
												'50%': { opacity: 0.3 },
											},
										}}
									/>
									<Typography
										variant="caption"
										sx={{
											fontWeight: 600,
											fontSize: '0.75rem',
											color: (() => {
												const hr = Number(formData.heartRate);
												if (hr >= 60 && hr <= 100) return '#166534';
												if (hr > 100 && hr <= 120) return '#854d0e';
												if ((hr >= 40 && hr < 60) || (hr > 120 && hr <= 150)) return '#9a3412';
												return '#991b1b';
											})(),
										}}
									>
										{(() => {
											const hr = Number(formData.heartRate);
											if (hr >= 60 && hr <= 100) return 'Normal Heart Rate';
											if (hr > 100 && hr <= 120) return 'Slightly Elevated';
											if (hr > 120) return 'Tachycardia (High)';
											if (hr >= 40 && hr < 60) return 'Bradycardia (Low)';
											return 'Critical - Seek Medical Attention';
										})()}
									</Typography>
								</Box>
							)}
						</Box>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: { xs: 1, sm: 2 },
								p: { xs: 1.5, sm: 2 },
								border: '2px solid #e5e7eb',
								borderRadius: '12px',
								backgroundColor: '#f9fafb',
								transition: 'all 0.2s ease',
								position: 'relative',
								overflow: 'hidden',
								flexDirection: { xs: 'column', sm: 'row' },
							}}
						>
							<Box sx={{ flex: 1, textAlign: 'center' }}>
								<Typography
									variant="caption"
									sx={{
										color: '#6b7280',
										fontWeight: 600,
										fontSize: '0.7rem',
										textTransform: 'uppercase',
										letterSpacing: '0.5px',
										display: 'block',
										mb: 0.5,
									}}
								>
									BPM
								</Typography>
								<TextField
									name="heartRate"
									type="number"
									placeholder="72"
									value={formData.heartRate}
									onChange={handleChange}
									variant="standard"
									inputProps={{ min: 40, max: 150, style: { textAlign: 'center' } }}
									sx={{
										'& input': {
											fontSize: { xs: '1.5rem', sm: '2rem' },
											fontWeight: 700,
											color: '#1f2937',
											padding: 0,
										},
										'& input::placeholder': {
											color: '#d1d5db',
											opacity: 1,
										},
									}}
								/>
							</Box>

							{/* Unit Label */}
							<Box
								sx={{
									bgcolor: '#f43f5e',
									color: 'white',
									px: 1.5,
									py: 1,
									borderRadius: '8px',
									fontWeight: 600,
									fontSize: '0.85rem',
									minWidth: '70px',
									textAlign: 'center',
								}}
							>
								beats/min
							</Box>
						</Box>
					</Box>


					<Box sx={{ mb: 3, mt: 1 }}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
							<Typography
								variant="subtitle1"
								sx={{
									color: '#374151',
									fontWeight: 500,
									fontSize: { xs: '0.95rem', sm: '1rem' }
								}}
							>
								Pain Scale *
							</Typography>
							<Box
								sx={{
									bgcolor: formData.painScale
										? `hsl(${120 - (formData.painScale * 12)}, 70%, 50%)`
										: '#e5e7eb',
									color: 'white',
									px: 2,
									py: 0.5,
									borderRadius: '12px',
									fontWeight: 600,
									fontSize: '1.1rem',
									minWidth: '50px',
									textAlign: 'center',
									boxShadow: formData.painScale
										? `0 4px 12px hsla(${120 - (formData.painScale * 12)}, 70%, 50%, 0.4)`
										: 'none',
									transition: 'all 0.3s ease',
								}}
							>
								{formData.painScale || 0}
							</Box>
						</Box>

						<Slider
							value={Number(formData.painScale) || 0}
							onChange={(e, value) => handleChange({ target: { name: 'painScale', value: value.toString() } })}
							min={0}
							max={10}
							step={1}
							marks={isMobile ? [
								{ value: 0, label: '0' },
								{ value: 5, label: '5' },
								{ value: 10, label: '10' },
							] : [
								{ value: 0, label: '0' },
								{ value: 2, label: '2' },
								{ value: 4, label: '4' },
								{ value: 6, label: '6' },
								{ value: 8, label: '8' },
								{ value: 10, label: '10' },
							]}
							sx={{
								'& .MuiSlider-thumb': {
									width: { xs: 20, sm: 24 },
									height: { xs: 20, sm: 24 },
									backgroundColor: '#fff',
									border: '3px solid currentColor',
									boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
									transition: 'all 0.3s ease',
									'&:hover, &.Mui-focusVisible': {
										boxShadow: `0 0 0 8px hsla(${120 - (formData.painScale * 12)}, 70%, 50%, 0.16)`,
										transform: 'scale(1.1)',
									},
									'&.Mui-active': {
										boxShadow: `0 0 0 12px hsla(${120 - (formData.painScale * 12)}, 70%, 50%, 0.16)`,
										transform: 'scale(1.2)',
									},
								},
								'& .MuiSlider-track': {
									height: { xs: 6, sm: 8 },
									borderRadius: '4px',
									background: formData.painScale
										? `linear-gradient(90deg, #10b981 0%, hsl(${120 - (formData.painScale * 12)}, 70%, 50%) 100%)`
										: '#10b981',
									border: 'none',
									transition: 'all 0.3s ease',
								},
								'& .MuiSlider-rail': {
									height: { xs: 6, sm: 8 },
									borderRadius: '4px',
									backgroundColor: '#e5e7eb',
									opacity: 1,
								},
								'& .MuiSlider-mark': {
									backgroundColor: '#fff',
									height: { xs: 10, sm: 12 },
									width: 2,
									'&.MuiSlider-markActive': {
										backgroundColor: '#fff',
									},
								},
								'& .MuiSlider-markLabel': {
									fontSize: { xs: '0.65rem', sm: '0.75rem' },
									color: '#6b7280',
									fontWeight: 500,
								},
								color: formData.painScale
									? `hsl(${120 - (formData.painScale * 12)}, 70%, 50%)`
									: '#10b981',
							}}
						/>

						<Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, px: 0.5 }}>
							<Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
								No Pain
							</Typography>
							<Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
								Worst Pain
							</Typography>
						</Box>
					</Box>

					<FormControl fullWidth sx={{ mb: 2 }}>
						<InputLabel id="mood-rating-label">Mood Rating</InputLabel>
						<Select
							labelId="mood-rating-label"
							name="moodRating"
							value={formData.moodRating}
							label="Mood Rating"
							onChange={handleChange}
						>
							<MenuItem value="happy">😊 Happy</MenuItem>
							<MenuItem value="sad">😞 Sad</MenuItem>
							<MenuItem value="angry">😡 Angry</MenuItem>
							<MenuItem value="disgust">🤢 Disgust</MenuItem>
							<MenuItem value="surprised">😲 Surprised</MenuItem>
							<MenuItem value="bad_low">😔 Bad / Low</MenuItem>
							<MenuItem value="none">❓ None of the above</MenuItem>
						</Select>
					</FormControl>

					<Box sx={{
						border: '2px solid #4caf50',
						borderRadius: { xs: '8px', sm: '10px' },
						p: { xs: 2, sm: 3 },
						mt: { xs: 2, sm: 3 },
						mb: { xs: 2, sm: 3 }
					}}>
						<Typography
							variant="h6"
							fontWeight={400}
							sx={{
								color: "#1f2937",
								mb: 2,
								fontSize: { xs: '1.05rem', sm: '1.2rem' }
							}}
						>
							Common Symptoms Assessment *
						</Typography>

						{[
							"Nausea",
							"Fatigue",
							"Anxiety",
							"Depression",
							"Appetite Loss"
						].map((symptom) => (
							<Box
								key={symptom}
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 2,
									mb: 2,
									flexWrap: 'wrap',
								}}
							>
								<Typography
									variant="subtitle1"
									sx={{
										color: "#374151",
										fontWeight: 500,
										width: { xs: '100%', sm: '140px' },
										flexShrink: 0,
										fontSize: { xs: '0.95rem', sm: '1rem' },
										mb: { xs: 0.5, sm: 0 }
									}}
								>
									{symptom}:
								</Typography>

								<Box sx={{ display: 'flex', flexGrow: 1, gap: { xs: 0.6, sm: 1.2 }, width: { xs: '100%', sm: 'auto' } }}>
									{["None", "Mild", "Moderate", "Severe"].map((level) => {
										const key = symptom.toLowerCase().replace(/\s/g, '');
										const isSelected = formData[key] === level;

										// Define colors for each level
										const levelColors = {
											None: { bg: '#10b981', border: '#059669', hover: '#059669' },
											Mild: { bg: '#3b82f6', border: '#2563eb', hover: '#2563eb' },
											Moderate: { bg: '#f59e0b', border: '#d97706', hover: '#d97706' },
											Severe: { bg: '#ef4444', border: '#dc2626', hover: '#dc2626' },
										};

										// Shorter labels for mobile
										const mobileLabel = level === "Moderate" ? "Mod." : level;

										return (
											<Button
												key={level}
												variant={isSelected ? "contained" : "outlined"}
												size="small"
												sx={{
													textTransform: "none",
													borderRadius: "12px",
													flex: 1,
													fontSize: { xs: '0.75rem', sm: '0.9rem' },
													fontWeight: isSelected ? 600 : 500,
													py: { xs: 1, sm: 1.2 },
													px: { xs: 0.5, sm: 1 },
													minWidth: 0,
													border: isSelected ? 'none' : `2px solid ${levelColors[level].border}`,
													color: isSelected ? 'white' : levelColors[level].bg,
													bgcolor: isSelected ? levelColors[level].bg : 'transparent',
													boxShadow: isSelected ? `0 4px 12px ${levelColors[level].bg}40` : 'none',
													transition: 'all 0.2s ease',
													whiteSpace: 'nowrap',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													'&:hover': {
														bgcolor: isSelected ? levelColors[level].hover : `${levelColors[level].bg}15`,
														borderColor: levelColors[level].hover,
														transform: 'translateY(-2px)',
														boxShadow: `0 6px 16px ${levelColors[level].bg}40`,
													},
													'&:active': {
														transform: 'translateY(0)',
													}
												}}
												onClick={() =>
													setFormData((prev) => ({
														...prev,
														[key]: level,
													}))
												}
											>
												{isMobile ? mobileLabel : level}
											</Button>
										);
									})}
								</Box>
							</Box>
						))}
					</Box>

					<FormControl fullWidth required sx={{ mb: 2 }}>
						<InputLabel id="sleep-quality-label">Sleep Quality</InputLabel>
						<Select
							labelId="sleep-quality-label"
							id="sleep-quality-select"
							name="sleepQuality"
							value={formData.sleepQuality}
							label="Sleep Quality"
							onChange={handleChange}
						>
							<MenuItem value="very_poor">😫 Very Poor</MenuItem>
							<MenuItem value="poor">😴 Poor</MenuItem>
							<MenuItem value="fair">😌 Fair</MenuItem>
							<MenuItem value="good">😊 Good</MenuItem>
							<MenuItem value="excellent">🌟 Excellent</MenuItem>
						</Select>
					</FormControl>
				</Box>
			</Box>


			<Box
				sx={{
					border: "2px solid #1976d2",
					borderRadius: { xs: '8px', sm: '10px' },
					p: { xs: 2, sm: 3 },
					mt: { xs: 2, sm: 3 },
					mb: { xs: 2, sm: 3 },
				}}
			>
				{/* Section Title */}
				<Typography
					variant="h6"
					fontWeight={500}
					sx={{
						color: "#1f2937",
						mb: 1,
						fontSize: { xs: '1.05rem', sm: '1.2rem' }
					}}
				>
					Assign Assessments for Patient Portal
				</Typography>

				{/* Subtitle */}
				<Typography variant="body2" sx={{
					color: "#6b7280",
					mb: { xs: 2, sm: 3 },
					fontSize: { xs: '0.85rem', sm: '0.875rem' }
				}}>
					Select which assessments the patient should complete in their portal:
				</Typography>

				{/* Assessment Boxes (stacked) */}
				{assessments.map((item) => {
					const isSelected = formData.assessment_type?.includes(item.key);

					const handleToggle = () => {
						setFormData((prev) => {
							const current = prev.assessment_type || [];
							const updated = isSelected
								? current.filter((val) => val !== item.key)
								: [...current, item.key];
							return { ...prev, assessment_type: updated };
						});
					};

					return (
						<Box
							key={item.key}
							onClick={handleToggle}
							sx={{
								border: isSelected ? "2px solid #1976d2" : "1.5px solid #d1d5db",
								borderRadius: "8px",
								p: { xs: 1.5, sm: 2 },
								display: "flex",
								alignItems: "flex-start",
								gap: { xs: 1.5, sm: 2 },
								mb: { xs: 1.5, sm: 2 },
								transition: "0.2s",
								cursor: "pointer",
								backgroundColor: isSelected ? "#f0f7ff" : "white",
								"&:hover": {
									borderColor: "#1976d2",
									boxShadow: "0 2px 8px rgba(25, 118, 210, 0.15)",
								},
							}}
						>
							<Checkbox
								checked={isSelected}
								value={item.key}
								name="assessment_type"
								sx={{ pointerEvents: "none" }} // prevent separate click handling
							/>

							<Box>
								<Typography
									variant="subtitle1"
									sx={{
										fontWeight: 600,
										color: "#111827",
										fontSize: { xs: '0.95rem', sm: '1rem' }
									}}
								>
									{item.title}
								</Typography>
								<Typography variant="body2" sx={{
									color: "#6b7280",
									fontSize: { xs: '0.8rem', sm: '0.875rem' }
								}}>
									{item.subtitle}
								</Typography>
							</Box>
						</Box>
					);
				})}

			</Box>


			<Box sx={{ display: 'flex', gap: 2 }}>
				<SubmitButton
					onClick={handleSubmit}
					fullWidth={isMobile}
					sx={{
						opacity: 1,
						cursor: "pointer",
						transition: "opacity 0.3s ease",
						fontSize: { xs: '0.9rem', sm: '1rem' },
						py: { xs: 1.2, sm: 1.5 },
						px: { xs: 2, sm: 3 },
					}}
				>
					{isMobile ? 'Create Session' : 'Create Session & Generate PIN'}
				</SubmitButton>
			</Box>
		</FormContainer >
	);
}

export default TreatmentSessions;
